import { Chip, TableCell, TableRow } from '@heroui/react';
import React from 'react';
import { FiTag } from 'react-icons/fi';

import {
  LoadingTable,
  type LoadingTableColumn,
} from '../../../../../../../components/Table';
import { type PromotionRow } from '../types';

interface PromotionTableProps {
  promotions: PromotionRow[];
  isLoading: boolean;
  /** Opens the read-only promotion detail view for the clicked row. */
  onSelectPromotion: (promotion: PromotionRow) => void;
}

const columns: LoadingTableColumn[] = [
  { label: 'Code', className: 'pl-6 min-w-32' },
  { label: 'Name', className: 'min-w-48' },
  { label: 'Enabled', className: 'min-w-24' },
  { label: 'Status', className: 'min-w-24' },
  { label: 'Begins', className: 'hidden md:table-cell min-w-28' },
  { label: 'Ends', className: 'hidden md:table-cell min-w-28' },
  { label: 'Tier mode', className: 'hidden lg:table-cell min-w-28' },
  { label: 'Tiers', className: 'hidden lg:table-cell min-w-20' },
  { label: 'Scope', className: 'hidden lg:table-cell min-w-28' },
  { label: 'Category', className: 'hidden xl:table-cell min-w-28' },
];

export const PromotionTable: React.FC<PromotionTableProps> = ({
  promotions,
  isLoading,
  onSelectPromotion,
}) => {
  const renderRow = (promotion: PromotionRow) => (
    <TableRow
      key={promotion.id}
      className="cursor-pointer transition-colors hover:bg-default-50 focus-visible:outline-2 focus-visible:outline-primary"
      tabIndex={0}
      aria-label={`View details for ${promotion.name}`}
      onClick={() => onSelectPromotion(promotion)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          // Space would otherwise scroll the page; Enter is a no-op default.
          event.preventDefault();
          onSelectPromotion(promotion);
        }
      }}
    >
      <TableCell className="pl-6">
        <span
          className="font-mono text-xs text-default-600 select-text cursor-text"
          onClick={(event) => event.stopPropagation()}
        >
          {promotion.code}
        </span>
      </TableCell>
      <TableCell>
        <span className="font-medium text-foreground">{promotion.name}</span>
      </TableCell>
      <TableCell>
        <Chip
          size="sm"
          variant="flat"
          color={promotion.isEnabled ? 'success' : 'default'}
        >
          {promotion.isEnabled ? 'Enabled' : 'Disabled'}
        </Chip>
      </TableCell>
      <TableCell>
        <Chip size="sm" variant="flat" color={promotion.status.tone}>
          {promotion.status.label}
        </Chip>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <span className="text-sm text-default-600">{promotion.beginsAt}</span>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <span className="text-sm text-default-600">{promotion.endsAt}</span>
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <span className="text-sm text-default-600">
          {promotion.tierModeLabel}
        </span>
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <span className="text-sm text-default-600">{promotion.tierCount}</span>
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <span className="text-sm text-default-600">{promotion.scopeLabel}</span>
      </TableCell>
      <TableCell className="hidden xl:table-cell">
        <span className="text-sm text-default-600">
          {promotion.categoryCode}
        </span>
      </TableCell>
    </TableRow>
  );

  return (
    <LoadingTable
      columns={columns}
      data={promotions}
      renderRow={renderRow}
      isLoading={isLoading}
      isEmpty={promotions.length === 0}
      rowCount={5}
      ariaLabel="Promotions table"
      loadingMessage="Loading promotions..."
      emptyState={{
        title: 'No promotions found',
        description:
          'This distributor has no promotions matching the current filter.',
        icon: FiTag,
      }}
    />
  );
};
